// Liquid-mercury avatar generator — a strict-TypeScript port of the FROZEN
// reference at reference/mercury-avatar-v1.js. It must stay BYTE-IDENTICAL to
// that reference: the image is f(seedString), so changing even one pixel
// changes a user's avatar. The golden vectors in reference/golden-v1.json pin
// every case (see mercury.test.ts). If the look ever needs to change, add a v2
// generator and a new seed namespace — never edit this to match a new look.
//
// Dependency-free on purpose: no imports. The module is portable to any JS
// runtime (browser canvas, Node) exactly like the reference it mirrors.

export type AvatarMask = 'circle' | 'none';

export interface AvatarParams {
  palette: number[][];
  angle: number;
  stretch: number;
  warp1: number;
  warp2: number;
  scale1: number;
  scale2: number;
  freq: number;
  carrier: number;
  phase: number;
  irid: number;
  o: number[];
  lightAngle: number;
}

// --- deterministic randomness ------------------------------------------------
function hash32(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number): () => number {
  return function (): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- value noise ---------------------------------------------------------------
function latticeHash(ix: number, iy: number): number {
  let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (((h ^ (h >>> 16)) >>> 0) & 0xffff) / 0xffff;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function noise2(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const a = latticeHash(ix, iy);
  const b = latticeHash(ix + 1, iy);
  const c = latticeHash(ix, iy + 1);
  const d = latticeHash(ix + 1, iy + 1);
  const ux = smooth(fx);
  const uy = smooth(fy);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

function fbm(x: number, y: number): number {
  let v = 0;
  let amp = 0.5;
  let fx = x;
  let fy = y;
  for (let o = 0; o < 3; o++) {
    v += amp * noise2(fx, fy);
    fx = fx * 2.03 + 17.1;
    fy = fy * 2.01 + 9.7;
    amp *= 0.5;
  }
  return v / 0.875; // ~0..1
}

// --- palettes: mercury families ---------------------------------------------
// Each is dark base -> mid metal -> ice -> iridescent tint -> near-white.
const PALETTES: string[][] = [
  // holographic blue/pink (ref 1)
  ['#0e1b2e', '#3d7bd9', '#9fd8ef', '#e9c8f2', '#f6f8fb'],
  // iridescent teal into pink
  ['#101826', '#2c9bb5', '#8fe8df', '#efa8d8', '#ffffff'],
  // deep blue satin (ref 4)
  ['#0a1224', '#163b8a', '#2f7bd0', '#9ac4f5', '#eaf4ff'],
  // violet chrome
  ['#151327', '#4a3f9e', '#8f7fe0', '#d9c9f0', '#ffffff'],
  // silver-teal chrome (ref 3)
  ['#0f1416', '#3a5f66', '#79b8c4', '#cfe9ef', '#ffffff'],
  // vivid holo (ref 2, tamed): magenta/cyan/amber on dark
  ['#120f1c', '#c433b8', '#33c3e8', '#e9a25c', '#ffe9fb'],
];

function hex(c: string): number[] {
  return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
}

function samplePalette(stops: number[][], t: number): number[] {
  const n = stops.length - 1;
  const x = Math.min(Math.max(t, 0), 1) * n;
  const i = Math.min(Math.floor(x), n - 1);
  const f = x - i;
  const a = stops[i] as number[];
  const b = stops[i + 1] as number[];
  return [
    (a[0] as number) + ((b[0] as number) - (a[0] as number)) * f,
    (a[1] as number) + ((b[1] as number) - (a[1] as number)) * f,
    (a[2] as number) + ((b[2] as number) - (a[2] as number)) * f,
  ];
}

// --- per-seed parameters --------------------------------------------------------
export function paramsFor(seedString: string): AvatarParams {
  const rand = mulberry32(hash32(seedString));
  const angle = rand() * Math.PI * 2;
  return {
    palette: (PALETTES[Math.floor(rand() * PALETTES.length)] as string[]).map(hex),
    angle,
    // anisotropy: how strongly ripples stretch along the flow direction
    stretch: 0.32 + rand() * 0.24,
    warp1: 0.55 + rand() * 0.55,
    warp2: 0.12 + rand() * 0.25,
    scale1: 0.8 + rand() * 0.7,
    scale2: 1.2 + rand() * 0.9,
    freq: 0.55 + rand() * 0.5,
    carrier: 0.7 + rand() * 1.2,
    phase: rand() * Math.PI * 2,
    irid: 0.35 + rand() * 0.5,
    o: Array.from({ length: 10 }, () => rand() * 40 - 20),
    lightAngle: -0.9 + rand() * 0.5,
  };
}

// The scalar surface height at a point (unit coords, roughly [-1,1]).
function surface(p: AvatarParams, x: number, y: number): number {
  const ca = Math.cos(p.angle);
  const sa = Math.sin(p.angle);
  // rotate into flow frame, compress across it -> elongated sheets
  const rx = (x * ca + y * sa) * p.stretch;
  const ry = -x * sa + y * ca;

  const w1x = fbm(rx * p.scale1 + (p.o[0] as number), ry * p.scale1 + (p.o[1] as number));
  const w1y = fbm(rx * p.scale1 + (p.o[2] as number), ry * p.scale1 + (p.o[3] as number));
  const qx = rx + p.warp1 * (w1x - 0.5) * 2;
  const qy = ry + p.warp1 * (w1y - 0.5) * 2;

  const w2x = fbm(qx * p.scale2 + (p.o[4] as number), qy * p.scale2 + (p.o[5] as number));
  const w2y = fbm(qx * p.scale2 + (p.o[6] as number), qy * p.scale2 + (p.o[7] as number));
  const rx2 = qx + p.warp2 * (w2x - 0.5) * 2;
  const ry2 = qy + p.warp2 * (w2y - 0.5) * 2;

  const n = fbm(rx2 * 1.15 + (p.o[8] as number), ry2 * 1.15 + (p.o[9] as number));
  const w = n * 3.1 + ry2 * p.carrier + p.phase;
  // two ripple harmonics: broad folds + finer wrinkles
  return 0.75 * Math.sin(w * p.freq * Math.PI) + 0.25 * Math.sin(w * p.freq * 1.9 * Math.PI + 1.4);
}

/**
 * Render into an RGBA buffer (Uint8ClampedArray, size*size*4).
 * `mask`: 'circle' (soft-edged disc, transparent corners) or 'none'.
 */
export function render(
  seedString: string,
  size: number,
  mask: AvatarMask,
): Uint8ClampedArray<ArrayBuffer> {
  const p = paramsFor(seedString);
  const out = new Uint8ClampedArray(size * size * 4);
  const step = 2 / size;
  const eps = step;

  const la = p.lightAngle;
  // light from upper area, slightly seed-turned; viewer on +z
  let lx = Math.cos(la) * 0.55;
  let ly = Math.sin(la) * 0.55 - 0.35;
  let lz = 0.76;
  const ll = Math.hypot(lx, ly, lz);
  lx /= ll;
  ly /= ll;
  lz /= ll;
  // half-vector with V=(0,0,1)
  let hx = lx,
    hy = ly,
    hz = lz + 1;
  const hl = Math.hypot(hx, hy, hz);
  hx /= hl;
  hy /= hl;
  hz /= hl;

  for (let j = 0; j < size; j++) {
    const y = -1 + (j + 0.5) * step;
    for (let i = 0; i < size; i++) {
      const x = -1 + (i + 0.5) * step;
      const idx = (j * size + i) * 4;

      const rr = Math.hypot(x, y);
      let alpha = 255;
      if (mask === 'circle') {
        if (rr > 1) {
          out[idx + 3] = 0;
          continue;
        }
        const edge = (1 - rr) / (step * 1.5);
        alpha = Math.round(255 * Math.min(1, edge));
      }

      const h0 = surface(p, x, y);
      const gx = (surface(p, x + eps, y) - surface(p, x - eps, y)) / (2 * eps);
      const gy = (surface(p, x, y + eps) - surface(p, x, y - eps)) / (2 * eps);
      // normal of the height field; nz tunes apparent relief depth
      let nx = -gx,
        ny = -gy,
        nz = 2.6;
      const nl = Math.hypot(nx, ny, nz);
      nx /= nl;
      ny /= nl;
      nz /= nl;

      const diff = Math.max(nx * lx + ny * ly + nz * lz, 0);
      const ndh = Math.max(nx * hx + ny * hy + nz * hz, 0);
      const spec = Math.pow(ndh, 130); // tight chrome glint
      const sheen = Math.pow(ndh, 9); // broad satin sheen
      const fres = Math.pow(1 - Math.max(nz, 0), 2); // grazing-angle iridescence

      // palette position: surface height + a touch of normal-driven shift
      const t = 0.46 + h0 * 0.3 + (diff - 0.5) * 0.18;
      // iridescence: pull the sample toward a hue-shifted second tap
      const t2 = t + 0.2 + 0.12 * Math.sin(nx * 4 + ny * 6);
      const c1 = samplePalette(p.palette, t);
      const c2 = samplePalette(p.palette, t2 - Math.floor(t2));
      const mixI = p.irid * (0.35 + 0.65 * fres);

      const shade = 0.22 + 0.8 * diff;
      let r = ((c1[0] as number) * (1 - mixI) + (c2[0] as number) * mixI) * shade;
      let g = ((c1[1] as number) * (1 - mixI) + (c2[1] as number) * mixI) * shade;
      let b = ((c1[2] as number) * (1 - mixI) + (c2[2] as number) * mixI) * shade;

      // chrome highlights push to near-white; sheen stays tinted
      r += spec * 235 + sheen * (c2[0] as number) * 0.35;
      g += spec * 240 + sheen * (c2[1] as number) * 0.35;
      b += spec * 245 + sheen * (c2[2] as number) * 0.35;

      // saturation push: the references are unapologetically chromatic
      const luma = 0.299 * r + 0.587 * g + 0.114 * b;
      r = luma + (r - luma) * 1.28;
      g = luma + (g - luma) * 1.28;
      b = luma + (b - luma) * 1.28;

      // gentle vignette grounds the disc
      const vig = 1 - 0.22 * rr * rr;
      out[idx] = r * vig;
      out[idx + 1] = g * vig;
      out[idx + 2] = b * vig;
      out[idx + 3] = alpha;
    }
  }
  return out;
}

/** Candidate i for a user: stable, pageable, wraps at 9 (3 pages of 3). */
export function candidateSeed(userId: string, index: number): string {
  return userId + ' ' + String(index % 9);
}
